import { Box, Card, Stack, Typography } from '@mui/material';

import { Button } from '@karehero/llama/components/atoms/Button/Button';
import { PageHeader } from '@karehero/llama/pwa/atoms/PageHeader/PageHeader';

import StatusBar from '@/designs-dev/device/StatusBar';
import ActionCard, { type Item } from '@/components/wrapped/ActionCard';
import UploadDocumentButton from '@/karehero-app/shell/UploadDocumentButton';
import DocRow from '@/karehero-app/care/DocRow';
import { type CallItem } from '@/karehero-app/care/fixtures';
import ScreenRoot from '@/components/wrapped/ScreenRoot';

const Section = ({ label }: { label: string }) => (
  <Box sx={{ mb: 1 }}>
    <Typography variant='overline' color='text.secondary'>
      {label}
    </Typography>
  </Box>
);

// The summary body — metadata header, summary (or the pending notice when
// notes haven't been written up yet), linked actions, uploads. Shared between
// the full-page subscreen (Calls tab) and the in-sheet push (linked call
// opened from an action).
export const CallSummaryContent = ({
  call,
  onToggleAction,
  onOpenAction,
}: {
  call: CallItem;
  onToggleAction: (id: number) => void;
  onOpenAction: (action: Item) => void;
}) => (
  <Stack spacing={2}>
    {/* call metadata: title + date */}
    <PageHeader
      variant='h2'
      title={call.title}
      description={`${call.when} · with ${call.withName}`}
    />

    <Box>
      <Section label='CALL SUMMARY' />
      {call.summary ? (
        <Card sx={{ p: 2 }}>
          <Typography variant='body1' color='text.secondary'>
            {call.summary}
          </Typography>
        </Card>
      ) : (
        // The same card the summary gets, on Floral White. It was a cherry
        // tint, which is a dark surface with dark type on it: the message was
        // there and could not be read. Waiting for a summary is not a state
        // worth colouring anyway.
        <Card sx={{ p: 2 }}>
          <Typography variant='body1'>
            Your call summary will appear here. You will be notified when it is
            ready.
          </Typography>
        </Card>
      )}
    </Box>

    {call.linkedActions.length > 0 && (
      <Box>
        <Section label='ACTIONS LINKED TO THIS CALL' />
        <Stack spacing={1.5}>
          {call.linkedActions.map((a) => (
            <ActionCard
              key={a.id}
              action={a}
              onToggle={onToggleAction}
              onOpen={onOpenAction}
            />
          ))}
        </Stack>
      </Box>
    )}

    {call.summary && (
      <Box>
        <Section label='UPLOADS' />
        <Stack spacing={1.5}>
          <UploadDocumentButton />
          {call.uploads.map((name, i) => (
            <DocRow key={i} doc={{ id: i, name }} />
          ))}
        </Stack>
      </Box>
    )}
  </Stack>
);

// The full-page call summary subscreen (Calls tab) — back bar + the shared
// content. Same spacing and patterns as the action designs.
const CallSummary = ({
  call,
  onBack,
  onToggleAction,
  onOpenAction,
}: {
  call: CallItem;
  // Pops to wherever the user came from — the routing lives with the caller,
  // the copy is just Back.
  onBack: () => void;
  onToggleAction: (id: number) => void;
  onOpenAction: (action: Item) => void;
}) => (
  <ScreenRoot>
    <StatusBar />

    <Box
      sx={{
        px: 2,
        py: 0.5,
        flexShrink: 0,
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      <Button
        variant='link'
        ariaLabel='Go back'
        iconLeft='chevron-left'
        onPress={onBack}
      >
        Back
      </Button>
    </Box>

    <Box sx={{ flex: 1, overflow: 'auto', px: 2, pt: 2, pb: 3 }}>
      <CallSummaryContent
        call={call}
        onToggleAction={onToggleAction}
        onOpenAction={onOpenAction}
      />
    </Box>
  </ScreenRoot>
);

export default CallSummary;
